import { Sparkles } from "lucide-react"

import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { Card } from "@/components/ui/card"

const renderText = (value: unknown) => {
  if (value instanceof Date) return value.toISOString()
  if (value == null) return ""
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  )
    return String(value)
  if (typeof value === "symbol") return value.toString()
  if (typeof value === "function")
    return value.name ? `[Function ${value.name}]` : "[Function]"
  try {
    return JSON.stringify(value)
  } catch {
    return Object.prototype.toString.call(value)
  }
}

type WeeksIntroCardProps = {
  weeksCount: number
  signupOpenCount: number
  nextPlannedWeek: string
  isFetching: boolean
  onRefresh: () => void
}

export function WeeksIntroCard({
  weeksCount,
  signupOpenCount,
  nextPlannedWeek,
  isFetching,
  onRefresh,
}: WeeksIntroCardProps) {
  return (
    <Card className="page-intro border-primary/10">
      <PageIntroSplitHeader
        kicker={
          <SectionKicker icon={<Sparkles />}>Week Operations</SectionKicker>
        }
        title="Schedule each war cycle with fewer misses and clearer state changes."
        titleClassName="max-w-4xl text-4xl leading-none md:text-5xl"
        description="Create upcoming week windows, move them through the valid lifecycle, and keep signup timing obvious for every admin."
        descriptionClassName="max-w-3xl text-base leading-7"
        actions={<RefreshButton isFetching={isFetching} onClick={onRefresh} />}
        metrics={
          <>
            <div className="metric-tile">
              <div className="metric-label">Tracked Weeks</div>
              <div className="metric-value">{weeksCount}</div>
              <div className="mt-2 text-sm text-muted-foreground">
                Historical and upcoming cycles in one view.
              </div>
            </div>
            <div className="metric-tile">
              <div className="metric-label">Signup Open</div>
              <div className="metric-value">{signupOpenCount}</div>
              <div className="mt-2 text-sm text-muted-foreground">
                Weeks currently accepting signup responses.
              </div>
            </div>
            <div className="metric-tile">
              <div className="metric-label">Next Focus</div>
              <div className="mt-2 text-lg font-semibold text-foreground">
                {renderText(nextPlannedWeek)}
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Suggested week ID based on the upcoming Monday window.
              </div>
            </div>
          </>
        }
      />
    </Card>
  )
}
